/**
 * Per-bot pricing configuration.
 *
 * URL: `/pricing?botId=<uuid>`. Если botId не задан или ботов нет — empty state.
 * Backend GET /api/partner/bots/{id}/pricing → {mode, markup_basis_points,
 * custom_prices_display_minor, plans[], usd_to_display_rate}. Поддерживаем 3 режима:
 *  - default  — наша платформенная цена (партнёр не накручивает)
 *  - percent  — наша цена × (1 + N/100). Применяется ко ВСЕМ планам.
 *  - custom   — индивидуальная цена для каждого плана В РУБЛЯХ (A2: не плавает от
 *    курса, клипается до себестоимости на покупке).
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton, SkeletonCard, SkeletonPage } from '@/components/ui/skeleton';
import {
  Bot,
  ArrowRight,
  Save,
  Loader2,
  Calendar,
  ChevronDown,
  Zap,
  AlertTriangle,
  Infinity as InfinityIcon,
} from 'lucide-react';
import { StarIcon } from '@/components/ui/star-icon';
import { PaymentProvidersCard, StarsShopCard } from '@/components/bot-settings/cards';
import { toast } from 'sonner';
import { SegmentControl } from '@/components/ui/segment-control';
import { api, type LifetimePricingBlock } from '@/lib/api';
import { normalizePeriodDays, periodLabel } from '@/lib/period';
import { getWebApp, haptic } from '@/lib/telegram';
import { Switch } from '@/components/ui/switch';
import { clampGraphemes, cn } from '@/lib/utils';

/** Доля партнёра в цене клиента при наценке `percent`: наценка считается от закупки,
 * поэтому +50% это треть цены, а не половина (вопрос партнёра 06.10.2026). */
function marginShareOf(percent: number): number {
  return Math.round((percent / (100 + percent)) * 100);
}

export const Route = createFileRoute('/pricing')({
  component: Pricing,
  validateSearch: (search: Record<string, unknown>) => ({
    botId: (search.botId as string | undefined) ?? null,
  }),
});

/** Русское склонение окончания слова «тариф»: 1 тариф, 2 тарифа, 5 тарифов. */
function plur(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return '';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'а';
  return 'ов';
}

type Mode = 'default' | 'percent' | 'custom';

const PERCENT_PRESETS = [0, 20, 50, 100, 200, 500, 1000];

// Кастомный слайдер: визуал из дизайна (gradient-трек + белый бегунок) +
// прозрачный нативный range сверху для реальной интерактивности.
// Макс наценка 1000% — бэк валидирует/клампит 0..100000 bp (MAX_MARKUP_BP,
// см. bot_pricing/resolver). Раньше было 100% и упиралось в «край».
const MARKUP_MAX_PERCENT = 1000;

function MarkupSlider({
  percent,
  onChange,
  label = 'Наценка %',
}: {
  percent: number;
  onChange: (p: number) => void;
  /** Имя для скринридера: слайдеров на экране несколько. */
  label?: string;
}) {
  const fill = `${Math.min(100, (percent / MARKUP_MAX_PERCENT) * 100)}%`;
  return (
    <div className="relative h-[18px] my-3.5 flex items-center">
      <div className="relative w-full h-2 rounded-full bg-white/10">
        <div
          className="absolute left-0 top-0 h-full rounded-full bg-white/70"
          style={{ width: fill }}
        />
        <div
          className="absolute top-1/2 w-[18px] h-[18px] rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.5)]"
          style={{ left: fill, transform: 'translate(-50%,-50%)' }}
        />
      </div>
      <input
        type="range"
        min={0}
        max={MARKUP_MAX_PERCENT}
        step={10}
        value={percent}
        onChange={(e) => onChange(Number(e.target.value))}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        aria-label={label}
      />
    </div>
  );
}

/** Запас к закупке «Навсегда» в режиме «свои цены», пока своя цена не задана.
 *  Тот же, что `LIFETIME_CUSTOM_FALLBACK_BP` в pricing/calculator.py. */
const LIFETIME_FALLBACK_BP = 5000;

/** Вверх до целого рубля: так бэк округляет розницу (`_round_up_to_whole_minor`). */
const ceilRub = (minor: number): number => Math.ceil(minor / 100) * 100;

/** `minor × bp / 10000` с обычным округлением, как `_scale_bp` на бэке. */
const scaleBp = (minor: number, bp: number): number => Math.round((minor * bp) / 10000);

/**
 * Цена «Навсегда» клиенту по режиму формы, в копейках. Формула бэка для рублёвой
 * закупки (`PricingCalculator._retail`): проценты считаются от рублей, без курса.
 * Разные формулы здесь и в боте показали бы партнёру цену, которой у клиента нет.
 */
function lifetimeRetailMinor(
  purchaseMinor: number,
  mode: Mode,
  markupBp: number,
  customMinor: number | undefined,
): number {
  if (mode === 'custom') {
    return ceilRub(
      customMinor !== undefined
        ? Math.max(customMinor, purchaseMinor)
        : scaleBp(purchaseMinor, 10000 + LIFETIME_FALLBACK_BP),
    );
  }
  if (mode === 'percent' && markupBp > 0) return ceilRub(scaleBp(purchaseMinor, 10000 + markupBp));
  return ceilRub(purchaseMinor);
}

/** «1 390 ₽», а с копейками «1 334,40 ₽»: закупка со скидкой бывает дробной. */
const rubText = (minor: number): string =>
  `${(minor / 100).toLocaleString('ru-RU', {
    minimumFractionDigits: minor % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })} ₽`;

function Pricing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { botId: searchBotId } = Route.useSearch();
  const qc = useQueryClient();

  const { data: bots, isPending: botsPending } = useQuery({
    queryKey: ['bots'],
    queryFn: () => api.bots(),
  });

  const selectedBotId = useMemo(() => {
    if (searchBotId) return searchBotId;
    if (bots && bots.length === 1) return bots[0].id;
    return null;
  }, [searchBotId, bots]);

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
  }, []);

  if (botsPending) {
    return (
      <SkeletonPage className="space-y-4">
        <h2 className="font-display text-[22px] font-medium px-1 pt-2">{t('Pricing table title')}</h2>
        <div className="space-y-2.5">
          <SkeletonCard className="h-[68px] rounded-[18px]" />
          <SkeletonCard className="h-[68px] rounded-[18px]" />
        </div>
      </SkeletonPage>
    );
  }

  if (!bots || bots.length === 0) {
    return (
      <div className="p-4 space-y-4 pb-24">
        <h2 className="font-display text-[22px] font-medium px-1 pt-2">{t('Pricing table title')}</h2>
        <div className="surface-card rounded-[24px] p-6 text-center space-y-3.5 flex flex-col items-center">
          <div className="w-14 h-14 rounded-2xl bg-white/[0.07] text-primary-light flex items-center justify-center">
            <Bot className="w-7 h-7" strokeWidth={1.6} />
          </div>
          <p className="text-muted-foreground text-sm">Сначала создайте бота, потом настроите цены.</p>
          <button
            type="button"
            onClick={() => navigate({ to: '/bots' })}
            className="bg-primary text-primary-foreground px-5 py-2.5 rounded-2xl font-semibold active:scale-[0.97] transition-transform"
          >
            {t('Create bot')}
          </button>
        </div>
      </div>
    );
  }

  if (!selectedBotId) {
    return (
      <div className="p-4 space-y-4 pb-24">
        <h2 className="font-display text-[22px] font-medium px-1 pt-2">{t('Pricing table title')}</h2>
        <p className="text-sm text-muted-foreground px-1">Цены настраиваются для каждого бота отдельно. Выберите:</p>
        <div className="space-y-2.5">
          {bots.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => {
                haptic('light');
                navigate({ to: '/pricing', search: { botId: b.id } });
              }}
              className="w-full surface-card rounded-[18px] p-4 flex items-center justify-between active:scale-[0.99] transition-transform"
            >
              <div className="min-w-0 text-left">
                <div className="font-display font-semibold truncate">{b.brandName || b.username}</div>
                <div className="font-mono text-xs text-dim truncate">@{b.username}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-dim shrink-0" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return <PricingEditor botId={selectedBotId} qc={qc} />;
}

function PricingEditor({ botId, qc }: { botId: string; qc: ReturnType<typeof useQueryClient> }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isPending } = useQuery({
    queryKey: ['pricing', botId],
    queryFn: () => api.pricing(botId),
    // Refetch по фокусу (в Telegram WebView его дёргает клавиатура/сворачивание)
    // прогоняет useEffect([data]) и молча сбрасывает несохранённые правки партнёра.
    refetchOnWindowFocus: false,
  });
  // Live CBR-курс с бэка — для конверсии USD-cents → RUB.
  const { data: stats } = useQuery({
    queryKey: ['partner-dashboard-stats'],
    queryFn: () => api.dashboardStats(),
  });
  const fxRate = stats?.fx_rate ?? 100;
  // Тот же ['fee-policy'], что и FeePolicyRow — react-query отдаёт один и тот же
  // закэшированный запрос, второго похода на бэк не будет.
  const { data: feePolicy } = useQuery({ queryKey: ['fee-policy'], queryFn: () => api.feePolicy() });

  const [mode, setMode] = useState<Mode>('default');
  const [markupBp, setMarkupBp] = useState(3000); // 30%
  const [customPrices, setCustomPrices] = useState<Record<string, number>>({});
  // Сырой текст инпутов «своей цены» (per-plan) — чтобы можно было стереть и набрать
  // заново. Клиппинг к platform-цене и пересчёт в usdMinor — на blur, не на keystroke.
  const [rubInputs, setRubInputs] = useState<Record<string, string>>({});
  const [trafficBp, setTrafficBp] = useState(0);
  // Брендинг имён тарифов: ключ — число устройств (строкой), значение — кастомное имя.
  const [nameOverrides, setNameOverrides] = useState<Record<string, string>>({});
  const [emojiOverrides, setEmojiOverrides] = useState<Record<string, string>>({});
  // Семейства, убранные с витрины бота. Ключ тот же — число устройств строкой.
  const [hiddenFamilies, setHiddenFamilies] = useState<string[]>([]);
  // Сроки и отдельные тарифы, снятые с продажи, и «Популярно» (#210, #214).
  const [hiddenPeriods, setHiddenPeriods] = useState<number[]>([]);
  const [hiddenCells, setHiddenCells] = useState<string[]>([]);
  const [popular, setPopular] = useState<string>('auto');
  // Раскрытие групп тарифов по периоду (по умолчанию открыты, кроме custom при многих планах).
  const [openGroups, setOpenGroups] = useState<Record<number, boolean>>({});
  // Раздел «Навсегда»: тумблер у бота и наценка на докупки. Своя цена живёт в общем
  // customPrices под plan_uuid тарифа: бэк перезаписывает эту карту целиком, и без
  // неё сохранение цен AdaptGroup молча стёрло бы цену «Навсегда».
  const [lifetimeOn, setLifetimeOn] = useState(true);
  const [lifetimeAddonBp, setLifetimeAddonBp] = useState(0);

  useEffect(() => {
    if (!data) return;
    setMode(data.mode);
    setMarkupBp(data.markup_basis_points);
    setCustomPrices({ ...data.custom_prices_display_minor });
    setRubInputs({});
    setTrafficBp(data.markup_basis_points_traffic ?? 0);
    setNameOverrides({ ...(data.plan_name_overrides ?? {}) });
    setEmojiOverrides({ ...(data.plan_emoji_overrides ?? {}) });
    setHiddenFamilies([...(data.disabled_plan_families ?? [])]);
    setHiddenPeriods([...(data.hidden_plan_periods ?? [])]);
    setHiddenCells([...(data.hidden_plan_cells ?? [])]);
    setPopular(data.popular_plan ?? 'auto');
    setLifetimeOn(data.lifetime?.enabled ?? true);
    setLifetimeAddonBp(data.lifetime?.addon_markup_bp ?? 0);
  }, [data]);

  const lifetime = data?.lifetime ?? null;

  // Замок ставится в обработчике MainButton и снимается по ответу: `saving` доходит
  // до ref только после рендера, и два нажатия подряд успевали отправить два PUT.
  const inFlightRef = useRef(false);
  const { mutate: save, isPending: saving } = useMutation({
    onSettled: () => {
      inFlightRef.current = false;
    },
    mutationFn: () =>
      api.savePricing(botId, {
        mode,
        markup_basis_points: mode === 'percent' ? markupBp : null,
        custom_prices_display_minor: mode === 'custom' ? customPrices : null,
        markup_basis_points_traffic: trafficBp,
        plan_name_overrides: nameOverrides,
        plan_emoji_overrides: emojiOverrides,
        disabled_plan_families: hiddenFamilies,
        hidden_plan_periods: hiddenPeriods,
        hidden_plan_cells: hiddenCells,
        popular_plan: popular,
        // Только то, что партнёр поменял. Наценка на докупки приходит уже разрешённой
        // (своя или дефолт платформы), и отправка без правки прибила бы дефолт к боту:
        // поменяй владелец его потом, этот бот остался бы на старом числе.
        ...(lifetime && lifetimeOn !== lifetime.enabled ? { lifetime_enabled: lifetimeOn } : {}),
        ...(lifetime && lifetimeAddonBp !== lifetime.addon_markup_bp
          ? { markup_basis_points_lifetime_addons: lifetimeAddonBp }
          : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pricing', botId] });
      qc.invalidateQueries({ queryKey: ['bots'] });
      haptic('success');
      toast.success(t('Saved'));
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message || 'Не сохранилось');
    },
  });

  // Свежие save/saving держим в ref'ах, чтобы обработчик MainButton был СТАБИЛЕН
  // (регистрируется один раз) — иначе при каждом ре-рендере вешался новый onClick
  // поверх старых (Telegram.MainButton.onClick добавляет, а не заменяет), и один тап
  // слал save() пачкой. Эти дубли гонялись наперегонки и перетирали наценку стейлым
  // набором — партнёр видел «наценка слетела». Подтверждено по audit_log на проде.
  const saveRef = useRef(save);
  saveRef.current = save;
  const savingRef = useRef(saving);
  savingRef.current = saving;

  // СОХРАНИТЬ активна только когда state загружен с бэка. Иначе тап по кнопке во
  // время загрузки (или при упавшем GET) отправлял НАЧАЛЬНЫЙ useState — mode='default',
  // custom_prices=null («не трогать») — и режим цен партнёра слетал на базовый при
  // живых custom-ценах в БД. Клиенты видели цены без наценки до пересохранения.
  const hasData = !!data;

  // `/pricing#payments`: сюда ведут «Способы оплаты» из вкладки «Бот» и старые ссылки.
  useEffect(() => {
    if (hasData && window.location.hash === '#payments') {
      document.getElementById('payments')?.scrollIntoView({ block: 'start' });
    }
  }, [hasData]);

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton || !tg?.MainButton) return;
    const onBack = () => navigate({ to: '/bot' });
    const onMain = () => {
      if (savingRef.current || inFlightRef.current) return; // не плодим параллельные сохранения
      inFlightRef.current = true;
      saveRef.current();
    };
    const timer = window.setTimeout(() => {
      tg.BackButton.show();
      tg.BackButton.onClick(onBack);
      if (hasData) {
        tg.MainButton.setText(t('Save').toUpperCase());
        tg.MainButton.show();
        tg.MainButton.onClick(onMain);
      }
    }, 300);
    return () => {
      window.clearTimeout(timer);
      tg.BackButton.offClick(onBack);
      tg.MainButton.offClick(onMain);
      tg.BackButton.hide();
      tg.MainButton.hide();
    };
  }, [navigate, t, hasData]);

  // Прогресс на MainButton, пока идёт сохранение (и визуально, и как доп. защита).
  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.MainButton) return;
    if (saving) tg.MainButton.showProgress();
    else tg.MainButton.hideProgress();
  }, [saving]);

  // Группировка планов по КАНОНИЧЕСКОМУ периоду (360 и 365 → один «12 месяцев»).
  // Внутри группы — по devices asc. ВАЖНО: хук ДО раннего return ниже — иначе при
  // переходе loading→loaded число хуков меняется (React error #310). null-safe по data.
  const plansByPeriod = useMemo(() => {
    const grouped: Record<number, NonNullable<typeof data>['plans']> = {};
    // Спецтарифы сюда не берём: цену им ставит владелец платформы, наценка партнёра
    // к ним не применяется вовсе. В брендинге ниже они, наоборот, нужны.
    for (const p of (data?.plans ?? []).filter((p) => !p.is_special)) {
      const canonical = normalizePeriodDays(p.days);
      (grouped[canonical] ||= []).push(p);
    }
    Object.values(grouped).forEach((arr) =>
      arr.sort((a, b) => a.devices - b.devices || a.plan_name.localeCompare(b.plan_name)),
    );
    return Object.fromEntries(Object.entries(grouped).sort(([a], [b]) => Number(a) - Number(b)));
  }, [data]);

  // «Семейства» тарифов для брендинга имён: уникальные по числу устройств
  // (2=Standard, 5=Pro, 15=Premium). Имена в AdaptGroup грязные и включают период,
  // поэтому группируем по devices. sample — имя плана с минимальным сроком (контекст).
  const families = useMemo(() => {
    const byDevices = new Map<
      number,
      { devices: number; sample: string; days: number; isSpecial: boolean }
    >();
    for (const p of data?.plans ?? []) {
      const cur = byDevices.get(p.devices);
      if (!cur || p.days < cur.days) {
        byDevices.set(p.devices, {
          devices: p.devices,
          sample: p.plan_name,
          days: p.days,
          isSpecial: Boolean(p.is_special),
        });
      }
    }
    return [...byDevices.values()].sort((a, b) => a.devices - b.devices);
  }, [data]);

  // Видимость тарифа по черновику: семейство, срок или отдельная клетка (#210).
  const cellOf = (p: { devices: number; days: number }) => `${p.devices}:${p.days}`;
  const isHidden = (
    p: { devices: number; days: number },
    st = { fam: hiddenFamilies, per: hiddenPeriods, cel: hiddenCells },
  ) => st.fam.includes(String(p.devices)) || st.per.includes(p.days) || st.cel.includes(cellOf(p));
  const regularPlans = (data?.plans ?? []).filter((p) => !p.is_special);
  /** Не даём снять с продажи последнее: витрина без тарифов это сломанный бот. */
  const leavesSomething = (st: { fam: string[]; per: number[]; cel: string[] }) =>
    regularPlans.length === 0 || regularPlans.some((p) => !isHidden(p, st));
  const toggleIn = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const togglePeriodGroup = (daysList: number[]) => {
    const allOff = daysList.every((d) => hiddenPeriods.includes(d));
    const next = allOff
      ? hiddenPeriods.filter((d) => !daysList.includes(d))
      : [...new Set([...hiddenPeriods, ...daysList])];
    if (!leavesSomething({ fam: hiddenFamilies, per: next, cel: hiddenCells })) {
      haptic('error');
      toast.error('Хотя бы один тариф должен продаваться');
      return;
    }
    haptic('light');
    setHiddenPeriods(next);
  };
  const toggleCell = (p: { devices: number; days: number }) => {
    const next = toggleIn(hiddenCells, cellOf(p));
    if (!leavesSomething({ fam: hiddenFamilies, per: hiddenPeriods, cel: next })) {
      haptic('error');
      toast.error('Хотя бы один тариф должен продаваться');
      return;
    }
    haptic('light');
    setHiddenCells(next);
  };
  // «Популярно» по черновику: та же логика, что у бэка (`pick_popular_devices`).
  const popularDevices = (() => {
    if (popular === 'none') return null;
    const sales = new Map<number, number>();
    for (const p of regularPlans) {
      if (isHidden(p)) continue;
      sales.set(p.devices, (sales.get(p.devices) ?? 0) + (p.sales_30d ?? 0));
    }
    if (sales.size === 0) return null;
    if (popular !== 'auto' && sales.has(Number(popular))) return Number(popular);
    return [...sales.entries()].sort((a, b) => b[1] - a[1] || b[0] - a[0])[0][0];
  })();
  const maxSales = Math.max(0, ...regularPlans.map((p) => p.sales_30d ?? 0));

  if (isPending || !data) {
    return (
      <SkeletonPage className="space-y-4">
        <Skeleton className="h-8 w-44 rounded-lg" />
        <SkeletonCard className="h-[118px] rounded-[22px]" />
        <Skeleton className="h-11 w-full rounded-full" />
        <div className="space-y-2.5">
          <SkeletonCard className="h-[76px] rounded-[18px]" />
          <SkeletonCard className="h-[76px] rounded-[18px]" />
          <SkeletonCard className="h-[76px] rounded-[18px]" />
        </div>
      </SkeletonPage>
    );
  }

  const markupPercent = Math.round(markupBp / 100);
  const trafficPercent = Math.round(trafficBp / 100);
  const defaultOpen = data.plans.length <= 6 || mode !== 'custom';
  const isOpen = (days: number) => openGroups[days] ?? defaultOpen;

  const usdToRub = (usdMinor: number) => Math.round((usdMinor * fxRate) / 100);
  // A2: custom-цены хранятся В РУБЛЯХ (копейки). Себестоимость плана в рублях по
  // текущему курсу — минимум для custom и «дно» клипа.
  const platformRubMinor = (plan: typeof data.plans[number]): number =>
    Math.round(plan.platform_price_minor * fxRate);

  // Live recompute розничной цены (в рублях-копейках) в зависимости от mode/markup/custom.
  const effectiveRetailRubMinor = (plan: typeof data.plans[number]): number => {
    const platRub = platformRubMinor(plan);
    if (mode === 'default') return platRub;
    if (mode === 'percent') return Math.round(platRub * (1 + markupBp / 10000));
    // custom: max(заданная_₽, себестоимость_₽) — цена не плавает, но не ниже себестоимости.
    return Math.max(customPrices[plan.plan_uuid] ?? 0, platRub);
  };

  // Платёжный сбор поверх цены — та же формула, что и на бэке (fee_policy.py
  // resolve_fee_policy + PaymentFees.flat_fee_minor): round-half-up процента, затем
  // округление вверх до рубля. Разная формула здесь и в счёте развела бы витрину и
  // чекаут на копейку. Наценку/маржу партнёра НЕ трогает — сбор платит клиент сверху.
  const feeSurchargeRubMinor = (baseRubMinor: number): number => {
    if (!feePolicy?.fee_included_in_price || baseRubMinor <= 0) return 0;
    const raw = Math.round((baseRubMinor * feePolicy.fee_percent) / 100);
    if (raw <= 0) return raw;
    return Math.ceil(raw / 100) * 100;
  };
  const withFeeRubMinor = (baseRubMinor: number): number => baseRubMinor + feeSurchargeRubMinor(baseRubMinor);

  const updateCustomRub = (planUuid: string, rubAmount: number, plan: typeof data.plans[number]) => {
    // Храним рубли напрямую; не даём опуститься ниже себестоимости в рублях.
    const displayMinor = Math.max(Math.round(rubAmount * 100), platformRubMinor(plan));
    setCustomPrices((prev) => ({ ...prev, [planUuid]: displayMinor }));
  };

  // Сводка: средняя маржа партнёра по всем планам (в рублях). Спецтарифы не в счёт:
  // у них жёсткая цена владельца и нулевая наценка партнёра по определению. Иначе
  // они же попадали в предупреждение «тарифы без вашей цены», хотя цену им не задать.
  const pricedPlans = data.plans.filter((p) => !p.is_special);
  const specialPlans = data.plans.filter((p) => p.is_special);
  const totalMarginRubMinor = pricedPlans.reduce((acc, p) => {
    return acc + Math.max(0, effectiveRetailRubMinor(p) - platformRubMinor(p));
  }, 0);
  const avgMarginRub =
    pricedPlans.length > 0 ? Math.round(totalMarginRubMinor / 100 / pricedPlans.length) : 0;
  // Наценка считается от закупки, маржа от цены клиенту: при наценке 80 % маржа 44 %.
  // Раньше здесь было одно слово «маржа» на рубли, а проценты не показывались вовсе.
  const pctOf = (f: (plat: number, retail: number) => number) =>
    pricedPlans.length > 0
      ? Math.round(
          (pricedPlans.reduce((acc, p) => acc + f(platformRubMinor(p), effectiveRetailRubMinor(p)), 0) /
            pricedPlans.length) *
            100,
        )
      : 0;
  const avgMarkupPct = pctOf((plat, retail) => (plat > 0 ? Math.max(0, retail - plat) / plat : 0));
  const avgMarginPct = pctOf((plat, retail) => (retail > 0 ? Math.max(0, retail - plat) / retail : 0));

  // Тарифы, которые в режиме «свои цены» продаются БЕЗ наценки (цена не задана или
  // не выше себестоимости в рублях). Клиент платит себестоимость — партнёр на них не
  // зарабатывает и об этом легко не знать. Показываем предупреждение.
  const customNoMarkupPlans =
    mode === 'custom'
      ? pricedPlans.filter((p) => (customPrices[p.plan_uuid] ?? 0) <= platformRubMinor(p))
      : [];

  return (
    <div className="p-4 space-y-4 pb-32">
      <div className="px-1 pt-2">
        <h2 className="font-display text-[22px] font-medium">Наценка</h2>
        <p className="text-[13px] text-muted-foreground mt-1 leading-relaxed">
          Сколько вы накручиваете сверх нашей платформенной цены. Клиент увидит итоговую сумму.
        </p>
      </div>

      {/* Сколько в среднем остаётся с подписки: цифра тонким Geologica, как на сводке. */}
      {mode !== 'default' && avgMarginRub > 0 && (
        <div className="relative overflow-hidden rounded-[22px] p-4 surface-card">
          <div className="relative z-10 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/[0.07] text-muted-foreground flex items-center justify-center shrink-0">
              <Zap className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="eyebrow">В среднем с подписки остаётся</div>
              <div className="font-display font-[330] text-2xl nums mt-0.5">
                +{avgMarginRub.toLocaleString('ru')} ₽
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground nums">
                наценка {avgMarkupPct}% · маржа {avgMarginPct}% от цены
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mode selector */}
      <div>
        <div className="eyebrow mb-2.5 px-1">Режим цен</div>
        <SegmentControl<Mode>
          value={mode}
          onChange={setMode}
          options={[
            { value: 'default', label: 'Базовая' },
            { value: 'percent', label: 'Наценка %' },
            { value: 'custom', label: 'Свои цены' },
          ]}
        />
      </div>

      {/* Режим «базовая» или наценка 0%: бот продаёт по себестоимости. Бэк принимает это
          молча, а партнёр узнавал о нулевой марже только из отчёта. Для «своих цен» есть
          свой баннер ниже, здесь только режимы без цен по тарифам. */}
      {(mode === 'default' || (mode === 'percent' && markupBp <= 0)) && (
        <div className="rounded-[18px] p-3.5 flex gap-3 border border-[rgba(245,180,60,0.3)] [background:linear-gradient(168deg,rgba(245,180,60,0.14),rgba(245,180,60,0.02)_60%,#141517)]">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" strokeWidth={2} />
          <div className="min-w-0 text-[13px] leading-relaxed">
            <div className="font-semibold text-amber-300">Бот продаёт по себестоимости</div>
            <div className="text-muted-foreground mt-0.5">
              Клиент платит ровно цену платформы, ваша маржа ноль. Укажите наценку или свои
              цены и сохраните.
            </div>
          </div>
        </div>
      )}

      {/* Предупреждение: в режиме «свои цены» есть тарифы без наценки (по себестоимости) */}
      {mode === 'custom' && customNoMarkupPlans.length > 0 && (
        <div className="rounded-[18px] p-3.5 flex gap-3 border border-[rgba(245,180,60,0.3)] [background:linear-gradient(168deg,rgba(245,180,60,0.14),rgba(245,180,60,0.02)_60%,#141517)]">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" strokeWidth={2} />
          <div className="min-w-0 text-[13px] leading-relaxed">
            <div className="font-semibold text-amber-300">
              {customNoMarkupPlans.length} тариф{plur(customNoMarkupPlans.length)} без вашей цены
            </div>
            <div className="text-muted-foreground mt-0.5">
              Клиент платит нашу себестоимость, и вы на них не зарабатываете. Задайте цену выше
              платформенной, иначе наценка не капает.
            </div>
          </div>
        </div>
      )}

      <FeePolicyRow />

      {/* Percent UI */}
      {mode === 'percent' && (
        <div className="surface-card rounded-[22px] p-[18px]">
          <div className="flex items-center justify-between">
            <span className="eyebrow">Наценка</span>
            <span className="font-display font-semibold text-[26px] text-primary-light">+{markupPercent}%</span>
          </div>
          <MarkupSlider percent={markupPercent} onChange={(p) => setMarkupBp(p * 100)} />
          <div className="flex flex-wrap gap-[7px]">
            {PERCENT_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setMarkupBp(p * 100)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  markupPercent === p
                    ? 'bg-white/[0.12] text-foreground'
                    : 'bg-white/[0.04] text-muted-foreground hover:bg-white/[0.08]'
                }`}
              >
                {p === 0 ? 'без наценки' : `+${p}%`}
              </button>
            ))}
          </div>
          {markupPercent > 0 && (
            <p className="mt-3 text-[12px] text-dim leading-relaxed">
              Из цены клиента ваших {marginShareOf(markupPercent)}%: наценка считается от
              закупки, а не от цены. При +100% вы получаете половину цены.
            </p>
          )}
        </div>
      )}

      {/* Plans list — сгруппированы по сроку */}
      <div className="space-y-3">
        <div className="eyebrow px-1">Тарифы, которые увидит клиент</div>
        {Object.entries(plansByPeriod).map(([daysKey, periodPlans]) => {
          const days = Number(daysKey);
          const open = isOpen(days);
          const periodCustomCount = periodPlans.filter((p) => customPrices[p.plan_uuid] !== undefined).length;
          const groupDays = [...new Set(periodPlans.map((p) => p.days))];
          const periodOff = groupDays.every((d) => hiddenPeriods.includes(d));
          return (
            <div key={days} className="surface-card rounded-[20px] overflow-hidden">
              <div className="flex items-center">
              <button
                type="button"
                onClick={() => setOpenGroups((prev) => ({ ...prev, [days]: !open }))}
                className="flex-1 min-w-0 flex items-center gap-2.5 pl-4 pr-2 py-[15px] text-left active:bg-white/[0.03] transition-colors"
              >
                <Calendar className="w-[17px] h-[17px] text-primary-light shrink-0" strokeWidth={1.8} />
                <span className={cn('font-semibold text-[14.5px] whitespace-nowrap', periodOff && 'text-dim line-through')}>
                  {periodLabel(days)}
                </span>
                <span className="text-[11.5px] text-dim">
                  {periodPlans.length} тариф{periodPlans.length === 1 ? '' : 'а'}
                  {mode === 'custom' && periodCustomCount > 0 && ` · ${periodCustomCount} своих`}
                </span>
                <ChevronDown
                  className={`ml-auto w-[18px] h-[18px] text-dim shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
                />
              </button>
              {/* Срок целиком: «не продавать 12 месяцев» (#210). Продление купленного
                  скрытие не трогает. */}
              <div className="pr-4 pl-1 flex items-center">
                <Switch
                  checked={!periodOff}
                  aria-label={`Продавать срок ${periodLabel(days)}`}
                  onCheckedChange={() => togglePeriodGroup(groupDays)}
                />
              </div>
              </div>
              {open && (
                <div className="px-3 pb-3 pt-1 space-y-[9px]">
                  {periodPlans.map((p) => {
                    const platformRub = Math.round(platformRubMinor(p) / 100);
                    const retailRubMinor = effectiveRetailRubMinor(p);
                    const retailRub = Math.round(retailRubMinor / 100);
                    const marginRub = retailRub - platformRub;
                    // Что реально спишется на чекауте — с сбором, если он включён в
                    // цену. Маржа партнёра считается от retailRub без сбора: его
                    // платит клиент, наценка не меняется.
                    const displayRetailRub = Math.round(withFeeRubMinor(retailRubMinor) / 100);
                    const customRubValue = customPrices[p.plan_uuid]
                      ? Math.round(customPrices[p.plan_uuid] / 100)
                      : platformRub;
                    const hidden = isHidden(p);
                    const byParent =
                      hiddenFamilies.includes(String(p.devices)) || hiddenPeriods.includes(p.days);
                    const sales = p.sales_30d ?? 0;
                    const isPopular = !hidden && popularDevices === p.devices && periodPlans.length > 1;
                    return (
                      <div
                        key={p.plan_uuid}
                        className={cn('surface-inset rounded-[15px] p-3.5 transition-opacity', hidden && 'opacity-55')}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold text-sm leading-tight truncate flex items-center gap-1.5">
                              {isPopular ? (
                                <StarIcon className="w-3.5 h-3.5 shrink-0 text-amber" aria-label="Популярно" />
                              ) : null}
                              <span className={cn('truncate', hidden && 'line-through')}>{p.plan_name}</span>
                            </div>
                            <div className="text-[12px] text-dim mt-0.5">{p.devices} устр.</div>
                            {/* Продажи за 30 дней: полоска той же длины у всех, заполнение
                                по самому продаваемому тарифу бота. */}
                            <div className="mt-1.5 flex items-center gap-1.5" aria-label={`Продано за 30 дней: ${sales}`}>
                              <span className="h-1 w-14 rounded-full bg-white/[0.06] overflow-hidden" aria-hidden>
                                <span
                                  className="block h-full rounded-full bg-white/70"
                                  style={{ width: `${maxSales > 0 ? Math.max(sales > 0 ? 8 : 0, (sales / maxSales) * 100) : 0}%` }}
                                />
                              </span>
                              <span className="text-[12px] text-dim nums">{sales} за 30 дн</span>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="eyebrow">Клиент платит</div>
                            <div className="font-display font-semibold text-xl nums leading-tight mt-0.5">
                              {displayRetailRub.toLocaleString('ru')} ₽
                            </div>
                            {marginRub > 0 && (
                              <div className="text-[12px] text-money-light font-semibold">
                                +{marginRub.toLocaleString('ru')} ₽ вам
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mt-2.5 flex items-center gap-2">
                          <button
                            type="button"
                            disabled={byParent}
                            aria-pressed={!hidden}
                            onClick={() => toggleCell(p)}
                            className={cn(
                              'rounded-full px-2.5 py-1 text-[12px] font-semibold transition-colors disabled:opacity-50',
                              hidden ? 'bg-white/5 text-dim' : 'bg-white/[0.07] text-foreground',
                            )}
                          >
                            {hidden
                              ? hiddenPeriods.includes(p.days)
                                ? 'Скрыт весь срок'
                                : hiddenFamilies.includes(String(p.devices))
                                  ? 'Скрыт весь тариф'
                                  : 'Скрыт'
                              : 'Виден'}
                          </button>
                          {!hidden && popular !== 'none' ? (
                            <button
                              type="button"
                              aria-pressed={popular === String(p.devices)}
                              onClick={() => {
                                haptic('light');
                                setPopular(popular === String(p.devices) ? 'auto' : String(p.devices));
                              }}
                              className={cn(
                                'rounded-full px-2.5 py-1 text-[12px] font-semibold transition-colors flex items-center gap-1',
                                popular === String(p.devices) ? 'bg-amber/20 text-amber' : 'bg-white/5 text-dim',
                              )}
                            >
                              <StarIcon filled={popular === String(p.devices)} className="w-3 h-3" />
                              {popular === String(p.devices) ? 'Популярный' : 'Сделать популярным'}
                            </button>
                          ) : null}
                        </div>
                        {mode === 'custom' ? (
                          <div className="mt-3">
                            <div className="eyebrow mb-1.5">Цена для клиента (₽)</div>
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                inputMode="numeric"
                                value={rubInputs[p.plan_uuid] ?? String(customRubValue)}
                                placeholder={`мин. ${platformRub.toLocaleString('ru')} ₽`}
                                onChange={(e) => {
                                  // Только цифры; пустую строку разрешаем (промежуточный ввод).
                                  const digits = e.target.value.replace(/[^0-9]/g, '');
                                  setRubInputs((prev) => ({ ...prev, [p.plan_uuid]: digits }));
                                }}
                                onBlur={(e) => {
                                  // Клиппинг к platform-цене и запись в usdMinor — на blur.
                                  const digits = e.target.value.replace(/[^0-9]/g, '');
                                  const parsed = digits === '' ? platformRub : parseInt(digits, 10);
                                  const clipped = Math.max(parsed, platformRub);
                                  updateCustomRub(p.plan_uuid, clipped, p);
                                  setRubInputs((prev) => ({ ...prev, [p.plan_uuid]: String(clipped) }));
                                }}
                                className="flex-1 bg-[#0f1012] border border-white/[0.08] rounded-xl px-3.5 py-2.5 text-[15px] font-display font-semibold nums focus:outline-none focus:border-white/30"
                              />
                              <span className="text-sm font-semibold text-dim">₽</span>
                            </div>
                          </div>
                        ) : (
                          <div className="text-[11.5px] text-dim mt-2.5">
                            Наша цена: <span className="font-semibold text-muted-foreground">{platformRub.toLocaleString('ru')} ₽</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* «Популярно» (#214): раньше бейдж всегда стоял на самом дорогом тарифе. */}
      <div className="surface-card rounded-[20px] p-4 space-y-2.5">
        <div className="flex items-center gap-1.5 font-semibold text-sm">
          <StarIcon className="w-4 h-4 text-amber" />
          Бейдж «Популярно»
        </div>
        <SegmentControl
          options={[
            { value: 'auto', label: 'Авто' },
            { value: 'own', label: 'Свой' },
            { value: 'none', label: 'Не показывать' },
          ]}
          value={popular === 'none' ? 'none' : popular === 'auto' ? 'auto' : 'own'}
          onChange={(v) => {
            haptic('light');
            if (v === 'auto') setPopular('auto');
            else if (v === 'none') setPopular('none');
            else if (popularDevices !== null) setPopular(String(popularDevices));
          }}
        />
        <p className="text-[12px] text-dim leading-relaxed">
          {popular === 'none'
            ? 'Бейджа нет ни в боте, ни в мини-аппе, ни на сайте.'
            : popular === 'auto'
              ? `На самом продаваемом тарифе за 30 дней${popularDevices !== null ? `: сейчас ${popularDevices} устр.` : ''}. Пока продаж нет, на самом большом.`
              : `Закреплён на тарифе ${popular} устр. Другой выбирается кнопкой «Сделать популярным» в карточке.`}
        </p>
      </div>

      {/* Спецтарифы: цену задаёт владелец платформы, наценка партнёра не применяется.
          Раньше строка рисовалась редактируемой: партнёр ставил 3 ₽, бэк молча
          выбрасывал, клиент видел прежние 10 ₽. */}
      {specialPlans.length > 0 && (
        <div className="space-y-2">
          <div className="eyebrow px-1">Спецтарифы</div>
          <div className="surface-card rounded-[20px] divide-y divide-white/[0.05] overflow-hidden">
            {specialPlans.map((p) => (
              <div key={p.plan_uuid} className="flex items-center justify-between gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[15px] truncate">{p.plan_name}</div>
                  <div className="text-[11.5px] text-dim mt-0.5">
                    {p.days} дн · {p.devices} устр.
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-semibold nums">
                    {Math.round(
                      withFeeRubMinor(p.fixed_retail_display_minor ?? effectiveRetailRubMinor(p)) / 100,
                    ).toLocaleString('ru')} ₽
                  </div>
                  <div className="text-[12px] text-dim">цена платформы</div>
                </div>
              </div>
            ))}
            <p className="px-4 py-3 text-[11.5px] text-dim">
              Цену задаёт платформа, наценка не применяется. Имя и значок тарифа можно
              поменять в брендинге ниже.
            </p>
          </div>
        </div>
      )}

      {/* «Навсегда»: второй поставщик, свой блок. Нет блока в ответе, значит раздел
          на платформе ещё не запущен, и показывать нечего. */}
      {lifetime && (
        <LifetimeCard
          block={lifetime}
          mode={mode}
          markupBp={markupBp}
          on={lifetimeOn}
          onToggle={(v) => {
            haptic('light');
            setLifetimeOn(v);
          }}
          customMinor={customPrices[lifetime.plan_uuid]}
          rawInput={rubInputs[lifetime.plan_uuid]}
          onRawInput={(digits) => setRubInputs((prev) => ({ ...prev, [lifetime.plan_uuid]: digits }))}
          onCustomCommit={(minor) =>
            setCustomPrices((prev) => {
              const next = { ...prev };
              if (minor === null) delete next[lifetime.plan_uuid];
              else next[lifetime.plan_uuid] = minor;
              return next;
            })
          }
          // Та же сверка, что у бэка: пока форма совпадает с сохранённым, берём его
          // цену (в ней учтены и правила владельца), иначе считаем превью сами.
          formMatchesSaved={
            mode === data.mode &&
            (mode !== 'percent' || markupBp === data.markup_basis_points) &&
            (mode !== 'custom' ||
              customPrices[lifetime.plan_uuid] ===
                (data.custom_prices_display_minor?.[lifetime.plan_uuid] ?? undefined))
          }
          addonBp={lifetimeAddonBp}
          onAddonBp={setLifetimeAddonBp}
          withFee={withFeeRubMinor}
        />
      )}

      {/* Brand plan names — переименование семейств тарифов под бренд партнёра */}
      <div className="surface-card rounded-[22px] p-[18px] space-y-3">
        <div>
          <div className="eyebrow">Тарифы у клиента</div>
          <p className="text-[12px] text-dim mt-1 leading-relaxed">
            Значок, название и показывать ли тариф вообще. Пусто: останется
            стандартное. Продление уже купленного тарифа скрытие не ломает.
          </p>
        </div>
        <div className="space-y-3">
          {families.map((f) => {
            // Спецтариф скрытием не управляется: старый сохранённый ключ его не прячет,
            // и подписывать строку «скрыт» было бы неправдой.
            const off = !f.isSpecial && hiddenFamilies.includes(String(f.devices));
            // Последний включённый выключить не даём: витрина без тарифов это
            // сломанный бот, и партнёр узнает об этом от первого же клиента.
            // Спецтарифы в счёт не идут: они живут отдельной кнопкой и скрытием
            // семейств не управляются.
            const lastOne =
              !off &&
              families.filter(
                (x) => !x.isSpecial && !hiddenFamilies.includes(String(x.devices)),
              ).length <= 1;
            return (
            <div key={f.devices}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className={cn('text-[12px]', off ? 'text-dim/60' : 'text-dim')}>
                  {f.devices} устр. · сейчас «{f.sample}»{off ? ' · скрыт' : ''}
                </div>
                {f.isSpecial ? (
                  // Тумблера видимости у спецтарифа нет намеренно: на витрине он
                  // рисуется отдельной кнопкой и `disabled_plan_families` его не
                  // касается. Кнопка «Скрыт» тут ничего бы не скрыла.
                  <span className="shrink-0 rounded-full px-2.5 py-1 text-[12px] font-semibold bg-white/5 text-dim">
                    Цена от платформы
                  </span>
                ) : (
                <button
                  type="button"
                  disabled={lastOne}
                  aria-pressed={!off}
                  aria-label={`Показывать тариф на ${f.devices} устройств`}
                  onClick={() => {
                    haptic('light');
                    setHiddenFamilies((prev) =>
                      off
                        ? prev.filter((x) => x !== String(f.devices))
                        : [...new Set([...prev, String(f.devices)])],
                    );
                  }}
                  className={cn(
                    'shrink-0 rounded-full px-2.5 py-1 text-[12px] font-semibold transition-colors',
                    'disabled:opacity-40',
                    off ? 'bg-white/5 text-dim' : 'bg-white/[0.07] text-foreground',
                  )}
                >
                  {off ? 'Скрыт' : 'Виден'}
                </button>
                )}
              </div>
              {lastOne && (
                <p className="text-[12px] text-dim mb-1.5 leading-snug">
                  Единственный тариф на витрине. Включите другой, чтобы скрыть этот.
                </p>
              )}
              {!off && (
              <div className="flex gap-2">
                {/* Значок стоит перед названием в списке тарифов у клиента. */}
                <input
                  type="text"
                  // Не maxLength: браузер считает UTF-16-юниты, и семья с ZWJ (11 юнитов)
                  // обрезалась посередине, превращаясь в мусор. Режем по графемам.
                  value={emojiOverrides[String(f.devices)] ?? ''}
                  placeholder="🛡"
                  aria-label={`Значок тарифа на ${f.devices} устройств`}
                  onChange={(e) => {
                    const v = clampGraphemes(e.target.value, 2);
                    setEmojiOverrides((prev) => {
                      const next = { ...prev };
                      if (v.trim()) next[String(f.devices)] = v.trim();
                      else delete next[String(f.devices)];
                      return next;
                    });
                  }}
                  className="w-14 shrink-0 surface-inset rounded-[13px] px-2 py-2.5 text-center text-[15px] focus:outline-none focus:border-white/30"
                />
                <input
                  type="text"
                  maxLength={128}
                  value={nameOverrides[String(f.devices)] ?? ''}
                  placeholder="Своё название (напр. Start)"
                  onChange={(e) => {
                    const v = e.target.value;
                    setNameOverrides((prev) => {
                      const next = { ...prev };
                      if (v.trim()) next[String(f.devices)] = v;
                      else delete next[String(f.devices)];
                      return next;
                    });
                  }}
                  className="flex-1 min-w-0 surface-inset rounded-[13px] px-3.5 py-2.5 text-sm font-semibold focus:outline-none focus:border-white/30"
                />
              </div>
              )}
            </div>
            );
          })}
        </div>
      </div>

      {/* Traffic top-up markup — независимая наценка на докупку GB */}
      <div className="surface-card rounded-[22px] p-[18px]">
        <div className="flex items-start justify-between gap-2.5">
          <div>
            <div className="text-[12.5px] text-muted-foreground font-semibold flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-primary-light" />
              Наценка на докупку трафика
            </div>
            <div
              className="text-[12px] text-dim mt-1"
              title="Когда клиент докупает GB к своей активной подписке, к нашей платформенной цене добавится ваша наценка %. Действует независимо от наценки на подписки."
            >
              Когда клиент докупает гигабайты
            </div>
          </div>
          <span className="font-display font-semibold text-2xl text-primary-light shrink-0">+{trafficPercent}%</span>
        </div>
        <MarkupSlider percent={trafficPercent} onChange={(p) => setTrafficBp(p * 100)} />
        <div className="flex flex-wrap gap-[7px]">
          {PERCENT_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setTrafficBp(p * 100)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                trafficPercent === p
                  ? 'bg-white/[0.12] text-foreground'
                  : 'bg-white/[0.04] text-muted-foreground hover:bg-white/[0.08]'
              }`}
            >
              {p === 0 ? 'без' : `+${p}%`}
            </button>
          ))}
        </div>

        {data.traffic_packs.length > 0 && (
          <div className="mt-4 space-y-1.5">
            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 px-1 text-[12.5px] text-dim">
              <span>Пакет</span>
              <span className="text-right">Закупка</span>
              <span className="text-right">Клиенту</span>
              <span className="text-right">Маржа</span>
            </div>
            {data.traffic_packs.map((pack) => {
              // Считаем ровно как бэк на пути клиента: усечение в USD-центах после
              // партнёрской наценки, и только потом перевод в рубли. Через «процент от
              // рублёвой себестоимости» числа разошлись бы с тем, что видит клиент.
              const costUsd = pack.platform_price_usd_minor;
              const retailUsd = Math.floor((costUsd * (10000 + trafficBp)) / 10000);
              const cost = usdToRub(costUsd);
              const retail = usdToRub(retailUsd);
              // «Клиенту» — со сбором, если он включён в цену; «Маржа» — от retail
              // без сбора, наценка партнёра не меняется.
              const retailWithFee = retail + feeSurchargeRubMinor(retail * 100) / 100;
              return (
                <div
                  key={pack.gb}
                  className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 items-baseline px-1 py-1 nums"
                >
                  <span className="text-[13px] font-semibold">{pack.gb} ГБ</span>
                  <span className="text-[13px] text-dim text-right">{cost} ₽</span>
                  <span className="text-[13px] font-semibold text-right">{retailWithFee} ₽</span>
                  <span className="text-[13px] text-primary-light text-right">
                    +{retail - cost} ₽
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="text-[12px] text-center text-dim flex items-center justify-center gap-1.5">
        <Zap className="w-3 h-3" />
        Цены пересчитываются по живому курсу ЦБ ({fxRate.toFixed(2)} ₽/$)
      </p>

      {/* Save button (fallback если TG MainButton не сработал) */}
      <button
        type="button"
        onClick={() => save()}
        disabled={saving}
        className="w-full bg-primary text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.28)] py-4 rounded-[18px] font-display font-semibold text-[15.5px] flex items-center justify-center gap-2.5 disabled:opacity-50 active:scale-[0.985] transition-transform"
      >
        {saving ? <Loader2 className="w-[18px] h-[18px] animate-spin" /> : <Save className="w-[18px] h-[18px]" />}
        {t('Save')}
      </button>

      {/* Способы оплаты переехали сюда из карточки бота: как на сайте, «Тарифы и оплата»
          одним экраном. Сохраняются сами, кнопка «Сохранить» выше их не касается. */}
      <section id="payments" className="scroll-mt-4 pt-2">
        <h2 className="mb-2 px-1 text-[12.5px] text-dim">Способы оплаты</h2>
        <div className="surface-card rounded-[20px] p-4 space-y-5">
          <PaymentProvidersCard botId={botId} />
          <StarsShopCard botId={botId} />
        </div>
      </section>

      <CatalogExtrasCard botId={botId} />
    </div>
  );
}

/** «−N%» за длинный срок в боте и спецтариф «Белые списки» (заявка #272). Сохраняются
 *  сразу, как и способы оплаты выше: это включатели, а не поля формы. */
function CatalogExtrasCard({ botId }: { botId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['bot', botId], queryFn: () => api.getBot(botId) });
  const mut = useMutation({
    mutationFn: (body: { period_discount_badge?: boolean; special_plans_enabled?: boolean }) =>
      api.updateBot(botId, body),
    onSuccess: () => {
      haptic('success');
      void qc.invalidateQueries({ queryKey: ['bot', botId] });
      toast.success('Сохранено');
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message || 'Не удалось сохранить');
    },
  });
  if (q.isPending) return <SkeletonCard className="h-36 rounded-[20px]" />;
  if (q.isError) return null;
  const pending = mut.isPending ? mut.variables : undefined;
  const discountOn = pending?.period_discount_badge ?? q.data.period_discount_badge !== false;
  const specialsOn = pending?.special_plans_enabled ?? q.data.special_plans_enabled !== false;
  return (
    <section id="catalog-extras" className="scroll-mt-4 pt-2">
      <div className="surface-card rounded-[20px] p-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[13.5px] font-semibold">Скидка за срок в боте</div>
            <p className="text-[11.5px] text-dim leading-relaxed mt-1">
              «−12%» у кнопок 3, 6 и 12 месяцев. Выключите, если цены у вас не про скидку.
            </p>
          </div>
          <Switch
            checked={discountOn}
            disabled={mut.isPending}
            aria-label="Скидка за срок в боте"
            onCheckedChange={(v: boolean) => mut.mutate({ period_discount_badge: v })}
          />
        </div>
        <div className="flex items-start justify-between gap-3 border-t border-white/10 pt-4">
          <div className="min-w-0">
            <div className="text-[13.5px] font-semibold">Тест белых списков</div>
            <p className="text-[11.5px] text-dim leading-relaxed mt-1">
              Спецтариф с ценой от платформы в боте, мини-аппе и на витрине. Выключите, и он
              пропадёт отовсюду, а купленные подписки доработают.
            </p>
          </div>
          <Switch
            checked={specialsOn}
            disabled={mut.isPending}
            aria-label="Тест белых списков"
            onCheckedChange={(v: boolean) => mut.mutate({ special_plans_enabled: v })}
          />
        </div>
      </div>
    </section>
  );
}


/** «до 15 устройств», «до 1 устройства». */
const devicesGenitive = (n: number): string =>
  n % 10 === 1 && n % 100 !== 11 ? 'устройства' : 'устройств';

/**
 * Раздел «Навсегда» у бота: продавать ли его, по чём и с какой наценкой докупки.
 *
 * Закупка у него фиксом в рублях, поэтому и цена клиенту считается от рублей: режим
 * цен бота к ней применяется, а курс нет. Докупки (трафик обхода, +15 устройств)
 * живут своей наценкой, отдельной от наценки на трафик AdaptGroup.
 */
function LifetimeCard({
  block,
  mode,
  markupBp,
  on,
  onToggle,
  customMinor,
  rawInput,
  onRawInput,
  onCustomCommit,
  formMatchesSaved,
  addonBp,
  onAddonBp,
  withFee,
}: {
  block: LifetimePricingBlock;
  mode: Mode;
  markupBp: number;
  on: boolean;
  onToggle: (v: boolean) => void;
  customMinor: number | undefined;
  rawInput: string | undefined;
  onRawInput: (digits: string) => void;
  /** null: своей цены нет, действует запас к закупке. */
  onCustomCommit: (minor: number | null) => void;
  formMatchesSaved: boolean;
  addonBp: number;
  onAddonBp: (bp: number) => void;
  withFee: (minor: number) => number;
}) {
  const purchase = block.platform_display_minor;
  // Своя цена вводится целыми рублями, а закупка со скидкой бывает с копейками:
  // пол поднимаем до рубля, иначе «ровно закупку» бэк отбил бы как цену ниже неё.
  const floorRub = Math.ceil(purchase / 100);
  const retail = formMatchesSaved
    ? block.retail_display_minor
    : lifetimeRetailMinor(purchase, mode, markupBp, customMinor);
  const margin = retail - purchase;
  const fallbackRub = ceilRub(scaleBp(purchase, 10000 + LIFETIME_FALLBACK_BP)) / 100;
  const addonPct = Math.round(addonBp / 100);
  const defaultAddonPct = Math.round(block.addon_markup_default_bp / 100);
  // Дефолт платформы среди пресетов: к нему возвращаются одним тапом.
  const presets = [...new Set([...PERCENT_PRESETS, defaultAddonPct])].sort((a, b) => a - b);
  const bypassGb = block.traffic_limit_bytes
    ? Math.round(block.traffic_limit_bytes / 1024 ** 3)
    : null;

  const howPriced =
    mode === 'custom'
      ? customMinor !== undefined
        ? 'Ваша цена «Навсегда». Ниже закупки её не поставить.'
        : `Своей цены нет, поэтому продаём с запасом +${LIFETIME_FALLBACK_BP / 100}% к закупке.`
      : mode === 'percent' && markupBp > 0
        ? `Закупка плюс ваша наценка ${Math.round(markupBp / 100)}%, с округлением вверх до рубля.`
        : 'Клиент платит ровно закупку, ваша маржа ноль. Поставьте наценку или свои цены.';

  return (
    <div className="surface-card rounded-[22px] p-[18px] space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <InfinityIcon className="w-[18px] h-[18px] text-primary-light shrink-0" strokeWidth={2} />
          <span className="font-display font-semibold text-[16px]">{block.plan_name}</span>
        </div>
        <p className="text-[11.5px] text-dim mt-1 leading-relaxed">
          Клиент платит один раз, срока нет. До {block.devices} {devicesGenitive(block.devices)},
          обычные серверы без лимита трафика
          {bypassGb ? `, обход белых списков ${bypassGb} ГБ (не сгорают)` : ''}.
        </p>
      </div>
      {/* Подпись у тумблера видимая: рядом с одним названием тарифа было непонятно,
          что он включает. */}
      <div className="flex items-center justify-between gap-3">
        <span className="text-[13.5px] font-semibold">Продавать в этом боте</span>
        <Switch
          checked={on}
          onCheckedChange={onToggle}
          aria-label="Продавать «Навсегда» в этом боте"
        />
      </div>

      {!on ? (
        <p className="text-[12px] text-dim leading-relaxed">
          Раздел выключен: клиенты этого бота его не видят. Уже купленные подписки работают
          как прежде. Не забудьте сохранить.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-[9px]">
            <div className="surface-inset rounded-[15px] p-3.5">
              <div className="eyebrow">Закупка</div>
              <div className="font-display font-semibold text-xl nums leading-tight mt-0.5">
                {rubText(purchase)}
              </div>
              <div className="text-[12px] text-dim mt-0.5">в рублях, от курса не зависит</div>
            </div>
            <div className="surface-inset rounded-[15px] p-3.5">
              <div className="eyebrow">Клиент платит</div>
              <div className="font-display font-semibold text-xl nums leading-tight mt-0.5">
                {Math.round(withFee(retail) / 100).toLocaleString('ru')} ₽
              </div>
              {margin > 0 ? (
                <div className="text-[12px] text-money-light font-semibold mt-0.5">
                  +{rubText(margin)} вам
                </div>
              ) : (
                <div className="text-[12px] text-dim mt-0.5">без вашей маржи</div>
              )}
            </div>
          </div>
          <p className="text-[11.5px] text-muted-foreground leading-relaxed">{howPriced}</p>

          {mode === 'custom' && (
            <div>
              <div className="eyebrow mb-1.5">Ваша цена «Навсегда» (₽)</div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label="Ваша цена «Навсегда» в рублях"
                  value={
                    rawInput ?? (customMinor !== undefined ? String(Math.round(customMinor / 100)) : '')
                  }
                  placeholder="не задана"
                  onChange={(e) => onRawInput(e.target.value.replace(/[^0-9]/g, ''))}
                  onBlur={(e) => {
                    // Как у тарифов выше: цена фиксируется на blur. Пусто значит «своей
                    // цены нет», и тогда работает запас к закупке, а не себестоимость.
                    const digits = e.target.value.replace(/[^0-9]/g, '');
                    if (digits === '') {
                      onCustomCommit(null);
                      onRawInput('');
                      return;
                    }
                    const clipped = Math.max(parseInt(digits, 10), floorRub);
                    onCustomCommit(clipped * 100);
                    onRawInput(String(clipped));
                  }}
                  className="flex-1 min-w-0 bg-[#0f1012] border border-white/[0.08] rounded-xl px-3.5 py-2.5 text-[15px] font-display font-semibold nums focus:outline-none focus:border-white/30 placeholder:font-sans placeholder:font-normal placeholder:text-[13px] placeholder:text-dim"
                />
                <span className="text-sm font-semibold text-dim">₽</span>
              </div>
              <p className="text-[12px] text-dim mt-1.5 leading-relaxed">
                Не ниже закупки: {floorRub.toLocaleString('ru')} ₽. Оставите пустым, продадим с
                запасом +{LIFETIME_FALLBACK_BP / 100}% к закупке, это {fallbackRub.toLocaleString('ru')} ₽.
              </p>
            </div>
          )}

          <div className="border-t border-white/[0.06] pt-4">
            <div className="flex items-start justify-between gap-2.5">
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold">Наценка на докупки</div>
                <p className="text-[12px] text-dim mt-1 leading-relaxed">
                  Когда клиент с «Навсегда» докупает трафик обхода или устройства. По
                  умолчанию {defaultAddonPct}%.
                </p>
              </div>
              <span className="font-display font-semibold text-2xl text-primary-light shrink-0">
                +{addonPct}%
              </span>
            </div>
            <MarkupSlider
              percent={addonPct}
              onChange={(p) => onAddonBp(p * 100)}
              label="Наценка на докупки к «Навсегда», %"
            />
            <div className="flex flex-wrap gap-[7px]">
              {presets.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => onAddonBp(p * 100)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    addonPct === p
                      ? 'bg-white/[0.12] text-foreground'
                      : 'bg-white/[0.04] text-muted-foreground hover:bg-white/[0.08]'
                  }`}
                >
                  {p === 0 ? 'без' : `+${p}%`}
                </button>
              ))}
            </div>

            {block.addons.length > 0 && (
              <div className="mt-4 space-y-1.5">
                <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 px-1 text-[12.5px] text-dim">
                  <span>Пакет</span>
                  <span className="text-right">Закупка</span>
                  <span className="text-right">Клиенту</span>
                  <span className="text-right">Маржа</span>
                </div>
                {block.addons.map((a) => {
                  const cost = a.platform_price_display_minor;
                  // Наценка поверх себестоимости докупки. «Клиенту» со сбором, если он
                  // в цене; маржа от цены без сбора: сбор платит клиент.
                  const addonRetail = scaleBp(cost, 10000 + addonBp);
                  return (
                    <div
                      key={a.key}
                      className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 items-baseline px-1 py-1 nums"
                    >
                      <span className="text-[13px] font-semibold">
                        {a.kind === 'devices'
                          ? `+${a.devices ?? 15} устр.`
                          : `${(a.gb ?? 0).toLocaleString('ru')} ГБ`}
                      </span>
                      <span className="text-[13px] text-dim text-right">
                        {Math.round(cost / 100).toLocaleString('ru')} ₽
                      </span>
                      <span className="text-[13px] font-semibold text-right">
                        {Math.round(withFee(addonRetail) / 100).toLocaleString('ru')} ₽
                      </span>
                      <span className="text-[13px] text-primary-light text-right">
                        +{Math.round((addonRetail - cost) / 100).toLocaleString('ru')} ₽
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}


/** Платёжный сбор в цене тарифа.
 *
 * Выключено — у каждого способа оплаты своя ставка, и надбавку человек видит только на
 * чекауте. Включено — сбор один на все способы и стоит прямо в цене каталога.
 * Платит его клиент в обоих случаях: наценка партнёра не трогается.
 */
function FeePolicyRow() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['fee-policy'], queryFn: () => api.feePolicy() });
  const mut = useMutation({
    mutationFn: (v: boolean) => api.setFeePolicy(v),
    onSuccess: (r) => {
      haptic('success');
      qc.setQueryData(['fee-policy'], r);
      toast.success(
        r.fee_included_in_price ? 'Цены показываются со сбором' : 'Сбор снова на чекауте',
      );
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message || 'Не удалось сохранить');
    },
  });
  if (!data) return null;
  return (
    <div className="surface-card rounded-[18px] p-4 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[13.5px] font-semibold">Сбор за оплату включён в цену</div>
        <p className="text-[11.5px] text-dim leading-relaxed mt-1">
          Клиент увидит одну цену, чем бы ни платил: сбор {data.fee_percent}% уже внутри.
          Выключено: у каждого способа своя ставка, и надбавка появляется на чекауте.
        </p>
        <p className="text-[11.5px] text-dim leading-relaxed mt-1">
          Ваша наценка не меняется. С баланса покупают без сбора: он берётся при пополнении.
        </p>
      </div>
      <Switch
        checked={data.fee_included_in_price}
        disabled={mut.isPending}
        aria-label="Сбор за оплату включён в цену"
        onCheckedChange={(v: boolean) => mut.mutate(v)}
      />
    </div>
  );
}
